import { Component } from '@theme/component';

/**
 * A custom element that renders a video background. With defer-loading, sources
 * attach only near the viewport and playback pauses offscreen or in hidden tabs.
 *
 * @typedef {object} Refs
 * @property {HTMLSourceElement[]} videoSources - The video sources.
 * @property {HTMLVideoElement} videoElement - The video element.
 *
 * @extends Component<Refs>
 */
export class VideoBackgroundComponent extends Component {
  requiredRefs = ['videoSources', 'videoElement'];

  /** @type {IntersectionObserver | undefined} */
  #observer;
  /** @type {AbortController | undefined} */
  #listeners;
  /** @type {MediaQueryList | undefined} */
  #reducedMotion;
  /** @type {HTMLVideoElement | undefined} */
  #video;
  /** @type {HTMLVideoElement | undefined} */
  #loadedVideo;
  #nearViewport = false;
  #userPaused = false;
  #userStarted = false;

  connectedCallback() {
    super.connectedCallback();
    this.#initialize();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#initialize();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#dispose();
  }

  #initialize() {
    this.#dispose();
    this.#video = this.refs.videoElement;

    if (!this.hasAttribute('defer-loading')) {
      this.#loadedVideo = undefined;
      this.#loadSources();
      return;
    }

    this.#video.muted = true;
    this.#listeners = new AbortController();
    const { signal } = this.#listeners;
    this.#reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.#reducedMotion.addEventListener('change', this.#updatePlayback, { signal });
    document.addEventListener('visibilitychange', this.#updatePlayback, { signal });
    this.#video.addEventListener('playing', () => this.setAttribute('data-video-playing', ''), { signal });
    this.#video.addEventListener('error', () => this.removeAttribute('data-video-playing'), { signal });

    if (!('IntersectionObserver' in window)) {
      this.#nearViewport = true;
      this.#updatePlayback();
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      if (this.#observer !== observer) return;
      this.#nearViewport = entries.some((entry) => entry.isIntersecting);
      this.#updatePlayback();
    }, { rootMargin: '300px 0px' });
    this.#observer = observer;
    observer.observe(this);
  }

  #loadSources() {
    const { videoSources, videoElement } = this.refs;
    let changed = false;

    for (const source of videoSources) {
      const { videoSource } = source.dataset;

      if (videoSource && source.getAttribute('src') !== videoSource) {
        source.setAttribute('src', videoSource);
        changed = true;
      }
    }

    if (changed || this.#loadedVideo !== videoElement) {
      videoElement.load();
      this.#loadedVideo = videoElement;
    }
  }

  #shouldPlay() {
    return this.isConnected && this.#nearViewport && !document.hidden && !this.#userPaused &&
      (!this.#reducedMotion?.matches || this.#userStarted);
  }

  /** Toggles playback from a review's play button, allowing intentional sound and motion. */
  togglePlayback() {
    if (!this.#video) return;
    this.#userPaused = !this.#video.paused;
    this.#userStarted = true;
    this.#nearViewport = true;
    this.#video.muted = false;
    this.#updatePlayback();
  }

  #updatePlayback = () => {
    if (!this.#shouldPlay()) {
      this.#video?.pause();
      return;
    }

    this.#loadSources();
    this.#play();
  };

  async #play() {
    const video = this.#video;
    if (!video || !video.paused) return;

    try {
      await video.play();
      if (this.#video !== video || !this.#shouldPlay()) video.pause();
    } catch {
      // Keep the poster when autoplay is blocked or the media cannot be loaded.
    }
  }

  #dispose() {
    this.#observer?.disconnect();
    this.#observer = undefined;
    this.#listeners?.abort();
    this.#listeners = undefined;
    this.#video?.pause();
    this.#video = undefined;
    this.#nearViewport = false;
    this.#userPaused = false;
    this.#userStarted = false;
    this.removeAttribute('data-video-playing');
  }
}

if (!customElements.get('video-background-component')) {
  customElements.define('video-background-component', VideoBackgroundComponent);
}
